import { ThemeEvents, CartAddEvent } from '@theme/events';
import { OfferCoordinator } from '@theme/offer-coordinator';

/**
 * Quantity Break — "buy more, save more" tier widget for the product page.
 *
 * Selecting a tier reveals one picker per unit in that tier, so the customer can
 * choose a different variant for each item. Add-to-cart adds every chosen variant
 * as its own cart line.
 *
 * The tier prices shown are PROJECTIONS from the current variant's price. The real
 * reduction has to be applied at checkout by a native Shopify automatic
 * quantity discount — keep the tier percentages in sync with it.
 */

const DEFAULT_MONEY_FORMAT = '${{amount}}';

/**
 * Formats cents using the shop's money_format string.
 * @param {number} cents
 * @param {string} format
 */
function money(cents, format) {
  const value = Number.isFinite(cents) ? cents : 0;
  const withDecimals = (separator, decimals = 2) => {
    const fixed = (value / 100).toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator === ',' ? '.' : ',');
    return fraction ? `${grouped}${separator}${fraction}` : grouped;
  };

  return (format || DEFAULT_MONEY_FORMAT).replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => {
    switch (token) {
      case 'amount':
        return withDecimals('.');
      case 'amount_no_decimals':
        return withDecimals('.', 0).split('.')[0];
      case 'amount_with_comma_separator':
        return withDecimals(',');
      case 'amount_no_decimals_with_comma_separator':
        return withDecimals(',', 0).split(',')[0];
      case 'amount_with_apostrophe_separator':
        return withDecimals('.').replace(/,/g, "'");
      default:
        return withDecimals('.');
    }
  });
}

/** @param {unknown} value */
function esc(value) {
  return String(value ?? '').replace(
    /[&<>"]/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char]
  );
}

class QuantityBreak extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    this.tiers = Array.from(this.querySelectorAll('[data-qb-tier]'));
    this.addBtn = this.querySelector('[data-add]');
    this.errorEl = this.querySelector('.qb__error');

    this.colorLabel = this.getAttribute('data-color-label') || 'Color';
    this.sizeLabel = this.getAttribute('data-size-label') || 'Size';
    this.moneyFormat = this.getAttribute('data-money-format') || DEFAULT_MONEY_FORMAT;
    // Only meaningful in colorway mode: when false each row is Color alone and the
    // colorway product's first available variant is used.
    this.showSize = this.getAttribute('data-show-size') !== 'false';
    this.baseCents = parseInt(this.getAttribute('data-base-price'), 10) || 0;
    this.singleVariant = parseInt(this.getAttribute('data-single-variant'), 10) || null;
    this.productId = this.getAttribute('data-product-id') || '';

    this.colors = this.#readJson('[data-qb-colors]');
    this.variants = this.#readJson('[data-qb-variants]');

    this.mode = this.colors.length ? 'colors' : this.variants.length > 1 ? 'sizes' : 'single';

    this.selected = this.tiers.find((tier) => tier.classList.contains('is-selected')) || this.tiers[0];

    for (const tier of this.tiers) {
      const head = tier.querySelector('.qb__tier-head');
      if (head) {
        head.addEventListener('click', () => this.#onTier(tier), { signal });
        head.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              this.#onTier(tier);
            }
          },
          { signal }
        );
      }
      // Interacting with the pickers must not bubble up and rebuild them.
      tier.querySelector('[data-qb-lines]')?.addEventListener('click', (event) => event.stopPropagation(), { signal });
    }

    this.addBtn?.addEventListener('click', () => this.onAdd(), { signal });

    // Keep the projections in step with Horizon's variant picker.
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.recompute();

    // If the page has Horizon's product form, route through its Add-to-cart button:
    // nothing is selected by default; picking a tier arms the shared coordinator.
    this.coordinated = Boolean(document.querySelector('product-form-component') && OfferCoordinator);
    this.offer = {
      label: () => this.offerLabel(),
      submit: () => this.onAdd(),
      deselect: () => this.deselectAll(),
    };

    if (this.coordinated) {
      if (this.addBtn) this.addBtn.hidden = true;
      this.deselectAll();
      OfferCoordinator.register(this.offer);
    } else {
      this.buildLines();
    }

    this.hideError();
  }

  disconnectedCallback() {
    if (this.coordinated && OfferCoordinator?.isActive(this.offer)) OfferCoordinator.clear(this.offer);
    this.#abortController.abort();
  }

  /** @param {string} selector */
  #readJson(selector) {
    try {
      const node = this.querySelector(selector);
      return node ? JSON.parse(node.textContent) : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  #onVariantUpdate = (event) => {
    if (this.mode === 'colors') return; // colorway prices come from the collection, not the picker
    const variant = event.detail?.resource;
    if (!variant) return;

    if (typeof variant.price === 'number') this.baseCents = variant.price;
    if (variant.id) this.singleVariant = variant.id;

    this.recompute();
    if (this.coordinated && OfferCoordinator.isActive(this.offer)) OfferCoordinator.refresh();
  };

  /** @param {HTMLElement} tier */
  #onTier(tier) {
    if (this.coordinated) {
      // Clicking the active tier again releases the offer and restores the normal button.
      if (tier === this.selected && OfferCoordinator.isActive(this.offer)) {
        this.deselectAll();
        OfferCoordinator.clear(this.offer);
        return;
      }
      this.select(tier);
      this.buildLines();
      OfferCoordinator.select(this.offer);
      return;
    }

    if (tier === this.selected) return; // already selected — keep its pickers intact
    this.select(tier);
    this.buildLines();
  }

  deselectAll() {
    this.selected = null;
    for (const tier of this.tiers) {
      tier.classList.remove('is-selected');
      const input = tier.querySelector('.qb__tier-input');
      if (input) input.checked = false;
      const lines = tier.querySelector('[data-qb-lines]');
      if (lines) lines.innerHTML = '';
    }
  }

  /** @param {HTMLElement} tier */
  select(tier) {
    this.selected = tier;
    for (const candidate of this.tiers) {
      const on = candidate === tier;
      candidate.classList.toggle('is-selected', on);
      const input = candidate.querySelector('.qb__tier-input');
      if (input) input.checked = on;
    }
    this.hideError();
  }

  selectedQty() {
    return parseInt(this.selected?.getAttribute('data-qty'), 10) || 1;
  }

  /** Price maths for one tier element. */
  #tierTotals(tier) {
    const base = this.basePrice();
    const qty = parseInt(tier.getAttribute('data-qty'), 10) || 1;
    const type = tier.getAttribute('data-type') || 'percent';
    const percent = parseFloat(tier.getAttribute('data-percent')) || 0;
    const fixed = parseInt(tier.getAttribute('data-fixed'), 10) || 0;

    const full = base * qty;
    let total;
    let unit;

    if (type === 'fixed' && fixed > 0) {
      total = fixed;
      unit = Math.round(fixed / qty);
    } else {
      unit = Math.round((base * (100 - percent)) / 100);
      total = unit * qty;
    }

    const save = full - total;
    return { qty, full, total, unit, save, off: full > 0 && save > 0 ? Math.round((save * 100) / full) : 0 };
  }

  offerLabel() {
    if (!this.selected) return '';
    const { qty, total } = this.#tierTotals(this.selected);
    return `Add ${qty} · ${money(total, this.moneyFormat)}`;
  }

  /** @param {number} index */
  lineHtml(index) {
    let fields = '';

    if (this.mode === 'colors') {
      fields +=
        `<label class="qb__field"><span class="qb__field-label">${esc(this.colorLabel)}</span>` +
        '<select class="qb__select" data-qb-line-color>' +
        this.colors
          .map((product) => {
            // With the size picker off the row resolves to one variant, so a colorway
            // with nothing in stock has to be disabled here instead.
            const soldOut = !this.showSize && !this.#firstAvailable(product);
            return (
              `<option value="${product.id}"${soldOut ? ' disabled' : ''}>` +
              `${esc(product.color)}${soldOut ? ' — Sold out' : ''}</option>`
            );
          })
          .join('') +
        '</select></label>';

      if (this.showSize) {
        fields +=
          `<label class="qb__field"><span class="qb__field-label">${esc(this.sizeLabel)}</span>` +
          '<select class="qb__select" data-qb-line-size></select></label>';
      }
    } else {
      fields +=
        `<label class="qb__field"><span class="qb__field-label">${esc(this.sizeLabel)}</span>` +
        '<select class="qb__select" data-qb-line-size>' +
        this.variants
          .map(
            (variant) =>
              `<option value="${variant.id}"${variant.available ? '' : ' disabled'}>` +
              `${esc(variant.title)}${variant.available ? '' : ' — Sold out'}</option>`
          )
          .join('') +
        '</select></label>';
    }

    return (
      '<div class="qb__line" data-qb-line>' +
      `<span class="qb__line-num">${index + 1}</span>` +
      `<div class="qb__line-fields">${fields}</div></div>`
    );
  }

  buildLines() {
    // Clear every tier's picker container, then fill only the selected one.
    for (const tier of this.tiers) {
      if (tier === this.selected) continue;
      const lines = tier.querySelector('[data-qb-lines]');
      if (lines) lines.innerHTML = '';
    }

    const container = this.selected?.querySelector('[data-qb-lines]');
    if (!container) return;

    if (this.mode === 'single') {
      container.innerHTML = '';
      return;
    }

    let html = '';
    for (let i = 0; i < this.selectedQty(); i++) html += this.lineHtml(i);
    container.innerHTML = html;

    for (const line of container.querySelectorAll('[data-qb-line]')) {
      const colorSelect = line.querySelector('[data-qb-line-color]');
      if (!colorSelect) continue;
      colorSelect.addEventListener('change', () => this.fillSizes(line));
      this.fillSizes(line);
    }

    this.hideError();
  }

  /**
   * First in-stock variant of a colorway product, falling back to its first variant.
   * @param {{variants: Array<{id: number, available: boolean}>}} product
   */
  #firstAvailable(product) {
    return product?.variants?.find((variant) => variant.available) ?? null;
  }

  /** @param {Element} line */
  fillSizes(line) {
    const colorSelect = line.querySelector('[data-qb-line-color]');
    const sizeSelect = line.querySelector('[data-qb-line-size]');
    if (!colorSelect || !sizeSelect) return;

    const id = parseInt(colorSelect.value, 10);
    const product = this.colors.find((candidate) => candidate.id === id) || this.colors[0];
    if (!product) return;

    sizeSelect.innerHTML = product.variants
      .map(
        (variant) =>
          `<option value="${variant.id}"${variant.available ? '' : ' disabled'}>` +
          `${esc(variant.size)}${variant.available ? '' : ' — Sold out'}</option>`
      )
      .join('');

    const firstAvailable = product.variants.find((variant) => variant.available);
    if (firstAvailable) sizeSelect.value = firstAvailable.id;
  }

  basePrice() {
    if (this.baseCents) return this.baseCents;
    if (this.colors[0]) return this.colors[0].price;
    if (this.variants[0]) return this.variants[0].price;
    return 0;
  }

  recompute() {
    for (const tier of this.tiers) {
      const { full, total, unit, save, off } = this.#tierTotals(tier);

      const unitEl = tier.querySelector('[data-qb-unit]');
      const totalEl = tier.querySelector('[data-qb-total]');
      const wasEl = tier.querySelector('[data-qb-was]');
      const offEl = tier.querySelector('[data-qb-off]');

      if (unitEl) unitEl.textContent = money(unit, this.moneyFormat);
      if (totalEl) totalEl.textContent = money(total, this.moneyFormat);
      if (wasEl) {
        wasEl.textContent = money(full, this.moneyFormat);
        wasEl.hidden = save <= 0;
      }
      if (offEl) {
        offEl.textContent = `-${off}%`;
        offEl.hidden = off <= 0;
      }
    }
  }

  /** @param {string} [message] */
  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.getAttribute('data-error-text') || 'Please choose an option for each item';
    this.errorEl.hidden = false;
  }

  hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }

  /** @param {Record<string, string>} properties */
  collectItems(properties) {
    if (this.mode === 'single') {
      if (!this.singleVariant) return null;
      return [{ id: this.singleVariant, quantity: this.selectedQty(), properties }];
    }

    const container = this.selected?.querySelector('[data-qb-lines]');
    if (!container) return null;

    // Colorway mode with the size picker off: the Color choice alone identifies the
    // variant, so resolve each row to that colorway's first in-stock variant.
    if (this.mode === 'colors' && !this.showSize) {
      const colorSelects = Array.from(container.querySelectorAll('[data-qb-line-color]'));
      if (!colorSelects.length) return null;

      const items = [];
      for (const select of colorSelects) {
        if (!select.value || select.selectedOptions[0]?.disabled) return null;
        const product = this.colors.find((candidate) => candidate.id === parseInt(select.value, 10));
        const variant = this.#firstAvailable(product);
        if (!variant) return null;
        items.push({ id: variant.id, quantity: 1, properties });
      }
      return items.length ? items : null;
    }

    const selects = Array.from(container.querySelectorAll('[data-qb-line-size]'));
    if (!selects.length) return null;

    const items = [];
    for (const select of selects) {
      const option = select.selectedOptions[0];
      if (!select.value || option?.disabled) return null;
      items.push({ id: parseInt(select.value, 10), quantity: 1, properties });
    }

    return items.length ? items : null;
  }

  async onAdd() {
    if (!this.selected) return;

    const percent = parseFloat(this.selected.getAttribute('data-percent')) || 0;
    // A shared group key on every line, so a Shopify Function or an ops export can
    // tell which lines were added as one offer.
    const properties = {
      _qb_group: `qb-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
    };
    const title = this.selected.querySelector('.qb__tier-title')?.textContent.trim();
    if (title) properties.Offer = title;
    if (percent > 0) properties._qb_percent = String(percent);

    const items = this.collectItems(properties);
    if (!items) {
      this.showError();
      return;
    }

    const restore = this.addBtn ? this.addBtn.textContent : null;
    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.textContent = this.getAttribute('data-adding-text') || 'Adding…';
    }

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body?.description || body?.message || 'Cart add failed');

      // Horizon's cart components re-render from the event; fetch the real cart so
      // the header bubble gets the true absolute count.
      let cart = {};
      try {
        cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      } catch (error) {
        console.error(error);
      }

      this.hideError();
      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'quantity-break', {
          source: 'quantity-break',
          productId: this.productId,
          itemCount: cart.item_count ?? items.reduce((sum, item) => sum + item.quantity, 0),
        })
      );
    } catch (error) {
      console.error(error);
      this.showError(this.getAttribute('data-cart-error-text') || 'Sorry, that could not be added to your cart.');
    } finally {
      if (this.addBtn) {
        this.addBtn.disabled = false;
        if (restore !== null) this.addBtn.textContent = restore;
      }
    }
  }
}

if (!customElements.get('quantity-break')) {
  customElements.define('quantity-break', QuantityBreak);
}
